import React, {useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import RegularFormSettings from './RegularFormSettings';
import TriggerFormSettings, {sanitizeTriggerRecipientFieldIds} from './TriggerFormSettings';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Tooltip from '@mui/material/Tooltip';
import {FORM_ACCESS_LEVELS} from '../../../../constants/formAccessLevels';
import {
    parseRemindIntervalInput,
    validateAccessLevel,
    validateFieldLength,
    validateGroupFilters
} from '../../../../utility/validationUtil';
import EmailsInput from '../../../../shared-components/EmailsInput';
import SettingsSectionBase from './SettingsSection';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeMotionOutlinedIcon from '@mui/icons-material/AutoAwesomeMotionOutlined';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import moment from 'moment/moment';
import FormExpirationDatePicker from '../FormExpirationDatePicker';
import useTextField from '../../../../utility/hooks/useTextField';
import CircleIcon from '@mui/icons-material/Circle';
import FORM_TYPE, {
    SETTINGS_FORM_TYPE_OPTIONS,
    getFormBehaviorFromType,
    getSettingsFormType
} from '../../../../constants/FormType';
import {
    PARENT_AUDIENCE_OPTION,
    PARENT_AUDIENCE_TYPE,
    mapParentAudienceOptionToType,
    mapParentAudienceTypeToOption,
} from '../../../../constants/ParentAudienceType';
import FormAccessSettings from '../FormAccessSettings';
import ApprovalSettings from './ApprovalSettings';
import AutoresponderSettings from './AutoresponderSettings';

import {
    LINKED_EMPLOYEE_AUDIENCE,
    LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE,
    contactPriorityQuestion,
    shouldShowContactPriority,
} from './contactPriorityVisibility';

const PARENT_WITH_ACCOUNT_AUDIENCE =
    `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|${PARENT_AUDIENCE_OPTION.withAccount}`;
const PARENT_WITHOUT_ACCOUNT_AUDIENCE =
    `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|${PARENT_AUDIENCE_OPTION.withoutAccount}`;
const PARENT_APPLICANT_STATUS_AUDIENCE =
    `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|${PARENT_AUDIENCE_OPTION.applicantStatus}`;
const PARENT_WITH_ACCOUNT_LABEL = 'Parent with an Admicity account';
const PARENT_WITHOUT_ACCOUNT_LABEL = 'Parent without an Admicity account';
const PARENT_APPLICANT_STATUS_LABEL = 'Parent of applicant with Applied or Offered status';
const SETTINGS_SIDE_CLEARANCE = 'clamp(160px, 16vw, 240px)';

const markItemsForDeletion = (items = []) =>
    (items || []).reduce((acc, item) => {
        if (!item || item.isNew) {
            return acc;
        }

        acc.push(
            item.isDeleted
                ? item
                : {
                    ...item,
                    isDeleted: true
                }
        );
        return acc;
    }, []);

const PrivateFormSettings = (
    {
        formId,
        name,
        onNameChange,
        expirationDate,
        onExpirationDateChange,
        remindInterval,
        onRemindIntervalChange,
        categories,
        onCategoriesChange,
        formCategories,
        isFormLive,
        isFormArchived,
        strictRecipientsFiltering,
        allParentsCanSubmit,
        sendToPriority1ContactsOnly,
        onRollOnly = false,
        visibleToParents,
        shareResponseWithOtherParents,
        showResponsesInLinkedProfile,
        onShowResponsesInLinkedProfileChange,
        showResponsesToLinkedParents,
        onShowResponsesToLinkedParentsChange,
        profileMustBeComplete,
        allowMultipleSubmissions,
        audience,
        parentAudienceType,
        audienceOptions,
        settings,
        originalRecipientSettings,
        externalUsers,
        accessLevel,
        notificationEmails,
        onSettingsChange,
        onSetStrictRecipientsFiltering,
        onAudienceChange,
        onParentAudienceTypeChange,
        onAllParentsCanSubmitChange,
        onSendToPriority1ContactsOnlyChange,
        onVisibleToParentsChange,
        onShareResponseWithOtherParentsChange,
        onProfileMustBeComplete,
        onAllowMultipleSubmissionsChange,
        onAccessLevelChange,
        onNotificationEmailsChange,
        enableRecipientNotifications,
        onEnableRecipientNotificationsChange,
        isCommunicationForm,
        onCommunicationTypeChange,
        canSwitchToEmail,
        enableTrigger,
        setEnableTrigger,
        formQuestionAssigment,
        linkToStudents,
        onLinkToStudentsChange,
        linkToEmployees,
        onLinkToEmployeesChange,
        hasInitialisedRecipients,
        allLinkedEmployeesCanSubmit,
        onAllLinkedEmployeesCanSubmitChange,
        formAccessRef,
        schema,
        enableAutoresponder,
        onAutoresponderToggle,
        recipientType

    }) => {
    const isCommunicationMode = isCommunicationForm === true;
    const getParentAudienceOptionFromValue = (value) => {
        const [, optionType] = String(value).split('|');

        return optionType || null;
    };
    const isLegacyParentAudience =
        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
        Number(parentAudienceType) === PARENT_AUDIENCE_TYPE.none;
    const selectedFormType = useMemo(() => getSettingsFormType({
        isCommunicationForm,
        allowMultipleSubmissions,
        enableTrigger,
        enableRecipientNotifications
    }), [
        isCommunicationForm,
        allowMultipleSubmissions,
        enableTrigger,
        enableRecipientNotifications
    ]);
    const getAudienceTypeFromValue = (value) => {
        if (value === LINKED_EMPLOYEE_AUDIENCE || value === LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE) {
            return FORM_AUDIENCE_TYPE.employees;
        }

        const [audienceId] = String(value).split('|');
        return Number(audienceId);
    };
    const getAccessLevelForAudienceValue = (value) => {
        const audienceType = getAudienceTypeFromValue(value);
        const optionType = getParentAudienceOptionFromValue(value);

        if (
            audienceType === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
            optionType == null
        ) {
            return accessLevel;
        }

        if (
            audienceType === FORM_AUDIENCE_TYPE.enquiries ||
            audienceType === FORM_AUDIENCE_TYPE.externalUsers ||
            audienceType === FORM_AUDIENCE_TYPE.previousSchool ||
            (
                audienceType === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
                (
                    optionType === PARENT_AUDIENCE_OPTION.withoutAccount ||
                    optionType === PARENT_AUDIENCE_OPTION.applicantStatus
                )
            )
        ) {
            return FORM_ACCESS_LEVELS.publicWithStudentLink;
        }

        return FORM_ACCESS_LEVELS.private;
    };
    const getAudienceOptionsForType = (formType) => {
        const includeLinkedEmployees = formType !== FORM_TYPE.emailCommunication;
        let hasAddedParentAudienceOptions = false;
        const baseOptions = audienceOptions.flatMap((option) => {
            if (option.value === FORM_AUDIENCE_TYPE.parentsForChildCompletion) {
                if (hasAddedParentAudienceOptions) {
                    return [];
                }

                hasAddedParentAudienceOptions = true;

                return [
                    ...(isLegacyParentAudience ? [option] : []),
                    {
                        value: PARENT_WITH_ACCOUNT_AUDIENCE,
                        label: PARENT_WITH_ACCOUNT_LABEL
                    },
                    {
                        value: PARENT_WITHOUT_ACCOUNT_AUDIENCE,
                        label: PARENT_WITHOUT_ACCOUNT_LABEL
                    },
                    {
                        value: PARENT_APPLICANT_STATUS_AUDIENCE,
                        label: PARENT_APPLICANT_STATUS_LABEL
                    }
                ];
            }

            if (
                option.value !== FORM_AUDIENCE_TYPE.employees ||
                !includeLinkedEmployees
            ) {
                return [option];
            }

            return [
                option,
                {
                    value: LINKED_EMPLOYEE_AUDIENCE,
                    label: 'Employee linked to student'
                },
                {
                    value: LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE,
                    label: 'Employee linked to employee'
                }
            ];
        });

        if (formType === FORM_TYPE.selfService) {
            return baseOptions.filter((option) => {
                const audienceType = getAudienceTypeFromValue(option.value);

                return (
                    audienceType === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
                    audienceType === FORM_AUDIENCE_TYPE.employees
                );
            });
        }

        if (formType === FORM_TYPE.triggerForm) {
            return baseOptions.filter((option) =>
                getAudienceTypeFromValue(option.value) !== FORM_AUDIENCE_TYPE.students
            );
        }

        return baseOptions;
    };
    const currentParentAudienceOption =
        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion
            ? mapParentAudienceTypeToOption(parentAudienceType)
            : null;
    const selectedAudienceValue =
        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion
             ? (isLegacyParentAudience
                 ? FORM_AUDIENCE_TYPE.parentsForChildCompletion
                 : `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|${currentParentAudienceOption ?? PARENT_AUDIENCE_OPTION.withAccount}`)
             : audience === FORM_AUDIENCE_TYPE.employees &&
             linkToStudents === true &&
            selectedFormType !== FORM_TYPE.emailCommunication
                 ? LINKED_EMPLOYEE_AUDIENCE
                 : audience === FORM_AUDIENCE_TYPE.employees &&
                 linkToEmployees === true &&
                 selectedFormType !== FORM_TYPE.emailCommunication
                     ? LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE
                     : audience;
    const audienceSelectOptions = useMemo(
        () => getAudienceOptionsForType(selectedFormType),
        [audienceOptions, isLegacyParentAudience, selectedFormType]
    );
    const isLiveFormTypeLocked =
        isFormLive &&
        (
            selectedFormType === FORM_TYPE.selfService ||
            selectedFormType === FORM_TYPE.triggerForm
        );
    const isCommunicationTypeLocked =
        selectedFormType === FORM_TYPE.emailCommunication &&
        (isFormLive || isFormArchived || !!linkToStudents || !!linkToEmployees);
    const shouldShowReminderSettings = selectedFormType !== FORM_TYPE.selfService;
    const hasAssignedRecipientsOrTrigger =
        settings && ([
                ...settings.groupFilters,
                ...settings?.studentFilters,
                ...settings?.applicationFilters,
                ...settings?.enquiryFilters,
                ...settings?.externalUserEmailAssignments,
                ...settings?.employeeFilters,
            ].filter(x => x.isDeleted == null || !x.isDeleted).length > 0 ||
            settings.triggerFormSettings?.selectedForm != null);
    const isAudienceSelectionLocked =
        isFormLive ||
        isFormArchived ||
        hasAssignedRecipientsOrTrigger;
    const isFormTypeSelectionLocked = hasAssignedRecipientsOrTrigger;
    const shouldShowBothParentsOption =
        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
        !allowMultipleSubmissions &&
        !isCommunicationMode;
    const shouldShowVisibleToParentsOption =
        shouldShowBothParentsOption &&
        accessLevel === FORM_ACCESS_LEVELS.publicWithStudentLink;
    const shouldShowShareResponseWithOtherParentsOption =
        shouldShowBothParentsOption &&
        accessLevel === FORM_ACCESS_LEVELS.private &&
        !allParentsCanSubmit;
    const shouldShowLinkedProfileVisibilityOption =
        selectedFormType === FORM_TYPE.selfService &&
        (selectedAudienceValue === LINKED_EMPLOYEE_AUDIENCE ||
            selectedAudienceValue === LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE);
    const shouldShowLinkedParentVisibilityOption =
        shouldShowLinkedProfileVisibilityOption &&
        selectedAudienceValue === LINKED_EMPLOYEE_AUDIENCE;
    const shouldShowPriority1ContactsOption = shouldShowContactPriority({
        audience,
        formType: selectedFormType,
        audienceValue: selectedAudienceValue
    });
    const priorityQuestion = contactPriorityQuestion({
        isCommunicationMode,
        formType: selectedFormType,
        audienceValue: selectedAudienceValue
    });
    const shouldShowProfileCompletionOption =
        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
        !isCommunicationMode;
    const shouldShowParentCompletionSection =
        shouldShowBothParentsOption ||
        shouldShowProfileCompletionOption ||
        shouldShowVisibleToParentsOption ||
        shouldShowShareResponseWithOtherParentsOption;
    const shouldShowApprovalSettings =
        !isCommunicationMode &&
        !!accessLevel &&
        ![
            FORM_TYPE.application,
            FORM_TYPE.shareable
        ].includes(selectedFormType);
    const formTypeOptions = useMemo(() => SETTINGS_FORM_TYPE_OPTIONS.map((option) => {
        let disabled = false;

        if (isFormArchived) {
            disabled = option.id !== selectedFormType;
        } else if (isCommunicationTypeLocked || isLiveFormTypeLocked) {
            disabled = option.id !== selectedFormType;
        } else if (isFormLive) {
            disabled =
                option.id === FORM_TYPE.selfService ||
                option.id === FORM_TYPE.triggerForm ||
                option.id === FORM_TYPE.emailCommunication;
        }

        if (!disabled && option.id === FORM_TYPE.emailCommunication) {
            disabled =
                !!linkToStudents ||
                (!isCommunicationMode && !canSwitchToEmail);
        }

        return {
            ...option,
            disabled
        };
    }), [
        canSwitchToEmail,
        isCommunicationMode,
        isCommunicationTypeLocked,
        isFormArchived,
        isFormLive,
        isLiveFormTypeLocked,
        linkToStudents,
        selectedFormType
    ]);
    const formTypeHelperText = useMemo(() => {
        if (isFormArchived) {
            return 'This form type cannot be changed on an archived form.';
        }

        if (isCommunicationTypeLocked) {
            return 'Email communication cannot be changed while linked to students or when the form is live.';
        }

        if (isLiveFormTypeLocked) {
            return 'This form type cannot be changed while the form is live.';
        }

        if (linkToStudents) {
            return 'Email communication is unavailable for employee-to-student linked forms.';
        }

        if (!isCommunicationMode && !canSwitchToEmail) {
            return 'Email communication is only available when the form contains email-only content.';
        }

        return ' ';
    }, [
        canSwitchToEmail,
        isCommunicationMode,
        isCommunicationTypeLocked,
        isFormArchived,
        isLiveFormTypeLocked,
        linkToStudents
    ]);

    useEffect(() => {
        if (isCommunicationMode && enableTrigger) {
            setEnableTrigger(false);
        }
    }, [isCommunicationMode, enableTrigger, setEnableTrigger]);

    const handleAudienceChange = (event) => {
        if (isAudienceSelectionLocked) {
            return;
        }

        const selectedAudienceValue = event.target.value;
        const isLinkedToStudentAudience = selectedAudienceValue === LINKED_EMPLOYEE_AUDIENCE;
        const isLinkedToEmployeeAudience = selectedAudienceValue === LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE;
        const [audienceId] = String(selectedAudienceValue).split('|');
        const newAudienceType = (isLinkedToStudentAudience || isLinkedToEmployeeAudience)
            ? FORM_AUDIENCE_TYPE.employees
            : Number(audienceId);
        const nextAccessLevel = getAccessLevelForAudienceValue(selectedAudienceValue);
        const nextParentAudienceType =
            newAudienceType === FORM_AUDIENCE_TYPE.parentsForChildCompletion
                ? mapParentAudienceOptionToType(getParentAudienceOptionFromValue(selectedAudienceValue))
                : newAudienceType === FORM_AUDIENCE_TYPE.enquiries
                    ? PARENT_AUDIENCE_TYPE.enquiry
                    : PARENT_AUDIENCE_TYPE.none;

        if (newAudienceType === FORM_AUDIENCE_TYPE.students) {
            setEnableTrigger(false);
        }

        onAudienceChange(newAudienceType);
        onParentAudienceTypeChange?.(nextParentAudienceType);
        onAccessLevelChange(nextAccessLevel);

        if (newAudienceType === FORM_AUDIENCE_TYPE.employees) {
            onLinkToStudentsChange?.(isLinkedToStudentAudience);
            onLinkToEmployeesChange?.(isLinkedToEmployeeAudience);
        } else {
            onLinkToStudentsChange?.(false);
            onLinkToEmployeesChange?.(false);
        }

        if (enableTrigger) {
            onSettingsChange((prev) => ({
                ...prev,
                triggerFormSettings: sanitizeTriggerRecipientFieldIds(
                    newAudienceType,
                    prev.triggerFormSettings ?? {}
                ),
            }));
        }
    };

    const handleFormTypeChange = (event) => {
        const nextFormType = event.target.value;
        const nextIsCommunicationMode = nextFormType === FORM_TYPE.emailCommunication;
        const {
            allowMultipleSubmissions: nextAllowMultipleSubmissions,
            enableRecipientNotifications: nextEnableRecipientNotifications,
            enableTrigger: nextEnableTrigger
        } = getFormBehaviorFromType(
            nextIsCommunicationMode
                ? FORM_TYPE.email
                : nextFormType
        );
        const nextAudienceOptions = getAudienceOptionsForType(nextFormType);
        const isSelectedAudienceStillAllowed = nextAudienceOptions.some(
            (option) => String(option.value) === String(selectedAudienceValue)
        );
        const shouldFallbackLinkedEmployeeAudience =
            (
                selectedAudienceValue === LINKED_EMPLOYEE_AUDIENCE ||
                selectedAudienceValue === LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE
            ) &&
            [
                FORM_TYPE.emailCommunication,
                FORM_TYPE.selfService
            ].includes(nextFormType);
        const canFallbackToEmployeeAudience =
            shouldFallbackLinkedEmployeeAudience &&
            nextAudienceOptions.some(
                (option) => String(option.value) === String(FORM_AUDIENCE_TYPE.employees)
            );
        const shouldResetAudience =
            !isSelectedAudienceStillAllowed &&
            !canFallbackToEmployeeAudience;

        onAllowMultipleSubmissionsChange(
            nextIsCommunicationMode
                ? false
                : nextAllowMultipleSubmissions
        );

        if (!nextIsCommunicationMode) {
            onEnableRecipientNotificationsChange(nextEnableRecipientNotifications);
        }

        if (nextIsCommunicationMode !== isCommunicationMode) {
            onCommunicationTypeChange?.(nextIsCommunicationMode);
        }

        setEnableTrigger(nextEnableTrigger);

        if (canFallbackToEmployeeAudience) {
            onAudienceChange(FORM_AUDIENCE_TYPE.employees);
            onParentAudienceTypeChange?.(PARENT_AUDIENCE_TYPE.none);
            onAccessLevelChange(getAccessLevelForAudienceValue(FORM_AUDIENCE_TYPE.employees));
            onLinkToStudentsChange?.(false);
            onLinkToEmployeesChange?.(false);
        } else if (shouldResetAudience) {
            onAudienceChange(FORM_AUDIENCE_TYPE.none);
            onParentAudienceTypeChange?.(PARENT_AUDIENCE_TYPE.none);
            onAccessLevelChange(getAccessLevelForAudienceValue(FORM_AUDIENCE_TYPE.none));
            onLinkToStudentsChange?.(false);
            onLinkToEmployeesChange?.(false);
        }

        onSettingsChange(prev => {
            const shouldClearRecipients = nextEnableTrigger || shouldResetAudience;
            const shouldClearLinkedRecipients =
                nextEnableTrigger || shouldResetAudience || canFallbackToEmployeeAudience;
            const nextGroupFilters = shouldClearRecipients
                ? markItemsForDeletion(prev.groupFilters)
                : prev.groupFilters;

            return {
                ...prev,
                groupFilters: nextGroupFilters,
                studentFilters: shouldClearRecipients
                    ? markItemsForDeletion(prev.studentFilters)
                    : prev.studentFilters,
                employeeFilters: shouldClearRecipients
                    ? markItemsForDeletion(prev.employeeFilters)
                    : prev.employeeFilters,
                applicationFilters: shouldClearRecipients
                    ? markItemsForDeletion(prev.applicationFilters)
                    : prev.applicationFilters,
                enquiryFilters: shouldClearRecipients
                    ? markItemsForDeletion(prev.enquiryFilters)
                    : prev.enquiryFilters,
                externalUserEmailAssignments: shouldClearRecipients
                    ? markItemsForDeletion(prev.externalUserEmailAssignments)
                    : prev.externalUserEmailAssignments,
                linkedGroupFilters: shouldClearLinkedRecipients
                    ? markItemsForDeletion(prev.linkedGroupFilters)
                    : prev.linkedGroupFilters,
                linkedStudentFilters: shouldClearLinkedRecipients
                    ? markItemsForDeletion(prev.linkedStudentFilters)
                    : prev.linkedStudentFilters,
                linkedApplicationFilters: shouldClearLinkedRecipients
                    ? markItemsForDeletion(prev.linkedApplicationFilters)
                    : prev.linkedApplicationFilters,
                linkedEnquiryFilters: shouldClearLinkedRecipients
                    ? markItemsForDeletion(prev.linkedEnquiryFilters)
                    : prev.linkedEnquiryFilters,
                linkedStrictFiltering: shouldClearLinkedRecipients
                    ? false
                    : prev.linkedStrictFiltering,
                validation: shouldClearRecipients
                    ? {
                        ...validateGroupFilters(nextGroupFilters),
                        ...validateAccessLevel(accessLevel, nextGroupFilters)
                    }
                    : prev.validation,
                triggerFormSettings: nextEnableTrigger
                    ? prev.triggerFormSettings
                    : {
                        ...prev.triggerFormSettings,
                        selectedForm: null,
                        selectedFormSchema: null,
                        relatedComponent: null,
                        targetState: null,
                        relatedFormEmailFieldId: null,
                        relatedFormEmployeeFieldId: null,
                    }
            };
        });
    };

    const nameField = useTextField({
        initialValue: name ?? '',
        validate: (value) => validateFieldLength(value, 128),
        onChange: (event) => onNameChange(event.target.value)
    });

    return (
        <Box sx={{
            py: 3,
            px: {
                xs: 3,
                md: SETTINGS_SIDE_CLEARANCE,
            }
        }}>
            <Box
                display="flex"
                flexDirection="column"
                sx={{
                    width: '100%',
                    maxWidth: '1800px',
                    mx: 'auto',
                    gap: 3,
                }}
            >
                <SettingsSectionBase
                    header="Form Details"
                    headerIcon={<DescriptionOutlinedIcon fontSize="small"/>}
                >
                    <Stack spacing={3}>
                        <TextField
                            fullWidth
                            label="Form Name"
                            required={nameField.required}
                            disabled={isFormArchived}
                            value={nameField.value}
                            onChange={nameField.onChange}
                            error={nameField.error}
                            helperText={nameField.helperText || ' '}
                            autoComplete="off"
                        />
                        {(() => {
                            const selectedCategories = categories || [];

                            const handleCategoryChange = (e) => {
                                onCategoriesChange(e.target.value);
                            };

                            return (
                                <TextField
                                    select
                                    fullWidth
                                    disabled={isFormArchived}
                                    value={selectedCategories}
                                    label="Categories"
                                    helperText=" "
                                    SelectProps={{
                                        multiple: true,
                                        renderValue: (selected) => {
                                            if (!formCategories?.length) {
                                                return <em>No categories</em>;
                                            }

                                            if (!Array.isArray(selected) || selected.length === 0) {
                                                return <em>No categories</em>;
                                            }

                                            const selectedCats = formCategories?.filter(c =>
                                                selected.includes(c.id)
                                            ) || [];

                                            return (
                                                <Stack flexDirection="row" gap={1} flexWrap="wrap">
                                                    {selectedCats.map(cat => (
                                                        <Box
                                                            key={cat.id}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.8,
                                                                padding: 1,
                                                                borderRadius: '18px',
                                                                border: '1px solid #adadad',
                                                            }}
                                                        >
                                                            <CircleIcon
                                                                fontSize="small"
                                                                sx={{color: cat.color}}
                                                            />
                                                            <Typography variant="body2">
                                                                {cat.name}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            );
                                        },
                                        MenuProps: {
                                            sx: {
                                                '& .MuiMenuItem-root': {
                                                    whiteSpace: 'unset',
                                                    wordBreak: 'break-all'
                                                }
                                            }
                                        }
                                    }}
                                    onChange={handleCategoryChange}
                                >
                                    {!formCategories?.length && (
                                        <MenuItem disabled>
                                            <em>No categories</em>
                                        </MenuItem>
                                    )}
                                    {formCategories?.map(c => (
                                        <MenuItem key={c.id} value={c.id}>
                                            <Checkbox checked={selectedCategories.includes(c.id)}/>

                                            <CircleIcon
                                                fontSize="small"
                                                sx={{
                                                    color: c.color,
                                                    mr: 1
                                                }}
                                            />

                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            );
                        })()}
                    </Stack>
                </SettingsSectionBase>
                <SettingsSectionBase
                    header="Form Type"
                    headerIcon={<AutoAwesomeMotionOutlinedIcon fontSize="small"/>}
                    subHeader="Choose how this form should behave."
                >
                    <TextField
                        select
                        fullWidth
                        label="Form Type"
                        value={selectedFormType}
                        onChange={handleFormTypeChange}
                        disabled={isFormTypeSelectionLocked}
                        helperText={formTypeHelperText}
                    >
                        {formTypeOptions.map((option) => (
                            <MenuItem
                                key={option.id}
                                value={option.id}
                                disabled={option.disabled}
                                sx={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {option.description}
                            </MenuItem>
                        ))}
                    </TextField>
                </SettingsSectionBase>
                <SettingsSectionBase
                    header={audience === FORM_AUDIENCE_TYPE.none ? 'Select audience' : 'Audience'}
                    headerIcon={<GroupsOutlinedIcon fontSize="small"/>}
                    subHeader={
                        audience === FORM_AUDIENCE_TYPE.none
                            ? (
                                selectedFormType === FORM_TYPE.emailCommunication
                                    ? 'Choose who will receive this email'
                                    : 'Choose who will receive and complete this form'
                            )
                            : null
                    }
                >
                    <TextField
                        select
                        fullWidth
                        label="Audience"
                        value={selectedAudienceValue}
                        onChange={handleAudienceChange}
                        disabled={isAudienceSelectionLocked}
                    >
                        <MenuItem disabled value={FORM_AUDIENCE_TYPE.none}>None</MenuItem>
                        {
                            audienceSelectOptions
                                .map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))
                        }
                    </TextField>
                </SettingsSectionBase>
                {audience === FORM_AUDIENCE_TYPE.none
                    ? null
                    : (<>
                        <SettingsSectionBase
                            header={'Settings'}
                            headerIcon={<SettingsOutlinedIcon fontSize="small"/>}
                            subHeader={
                                audience === FORM_AUDIENCE_TYPE.none
                                    ? 'Choose who will receive and complete this form'
                                    : null
                            }
                        >
                            <Stack spacing={3}>
                                {shouldShowReminderSettings && (
                                    <FormExpirationDatePicker
                                        label="Expiration Date (optional)"
                                        expirationDate={expirationDate}
                                        onExpirationDateChange={onExpirationDateChange}
                                        disabled={isFormArchived}
                                        minDateTime={
                                            settings.scheduledActivationAt
                                                ? moment(settings.scheduledActivationAt)
                                                : moment()
                                        }
                                    />
                                )}
                                {shouldShowReminderSettings && !isCommunicationMode && (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Remind interval"
                                        variant="outlined"
                                        disabled={isFormArchived}
                                        value={remindInterval}
                                        onChange={(e) => {
                                            const nextValue = parseRemindIntervalInput(e.target.value);
                                            if (nextValue === null) {
                                                return;
                                            }
                                            onRemindIntervalChange(nextValue);
                                        }}
                                        helperText="In hours (0–1000, 0 = Disabled)"
                                        inputProps={{
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*'
                                        }}
                                    />
                                )}
                                {!isCommunicationMode && (
                                    <EmailsInput
                                        label="Email address to be notified when this form receives submissions"
                                        values={notificationEmails}
                                        onChange={emails =>
                                            onNotificationEmailsChange(
                                                Array.from(new Map(emails.map(item => [item.email, item])).values())
                                            )
                                        }
                                        onBlur={email =>
                                            onNotificationEmailsChange(prev =>
                                                Array.from(new Map([
                                                    ...prev.map(item => [item.email, item]),
                                                    [email, {email}],
                                                ]).values())
                                            )
                                        }
                                        disabled={isFormArchived}
                                    />
                                )}
                            </Stack>
                        </SettingsSectionBase>

                    </>)
                }
                {shouldShowPriority1ContactsOption && (
                    <SettingsSectionBase
                        header="Contact Priority"
                        headerIcon={<SupervisorAccountIcon fontSize="small"/>}
                    >
                        <Box>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                ml: 1,
                                mb: 0.5
                            }}>
                                {priorityQuestion}
                                <Tooltip
                                    title="Only contacts with parental responsibility and no legal order are eligible."
                                    placement="right"
                                >
                                    <InfoOutlinedIcon
                                        fontSize="small"
                                        sx={{
                                            color: 'text.secondary',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </Tooltip>
                            </Box>
                            <RadioGroup
                                value={sendToPriority1ContactsOnly ? 'true' : 'false'}
                                onChange={async (e) =>
                                    await onSendToPriority1ContactsOnlyChange(e.target.value === 'true')
                                }
                            >
                                <FormControlLabel
                                    sx={{gap: 1, mr: 0, ml: 0}}
                                    value="true"
                                    disabled={isFormLive || isFormArchived}
                                    control={
                                        <Radio sx={{
                                            padding: 0,
                                            marginLeft: 1
                                        }}/>
                                    }
                                    label="Priority 1 contacts only"
                                />
                                <FormControlLabel
                                    sx={{gap: 1, mr: 0, ml: 0}}
                                    value="false"
                                    disabled={isFormLive || isFormArchived}
                                    control={
                                        <Radio sx={{
                                            padding: 0,
                                            marginLeft: 1
                                        }}/>
                                    }
                                    label="Priority 1 and 2 contacts"
                                />
                            </RadioGroup>
                        </Box>
                    </SettingsSectionBase>
                )}
                {shouldShowParentCompletionSection && (
                    <SettingsSectionBase
                        header="Parent Completion"
                        headerIcon={<SupervisorAccountIcon fontSize="small"/>}
                    >
                        <Stack spacing={2}>
                            {shouldShowBothParentsOption && (
                                <FormControlLabel
                                sx={{
                                    gap: 1,
                                }}
                                control={
                                    <Checkbox
                                        sx={{
                                            padding: 0,
                                            marginLeft: 1
                                        }}
                                        checked={allParentsCanSubmit}
                                        onChange={async (e) =>
                                            await onAllParentsCanSubmitChange(e.target.checked)
                                        }
                                        disabled={isFormLive || isFormArchived}
                                    />
                                }
                                label={
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                    }}>
                                        Require both parents to complete the form
                                        <Tooltip
                                            title="When ticked, all eligible contacts receive a form to complete individually for the linked profile. When unticked, one form is sent to all, and the first to submit completes it."
                                            placement="right"
                                        >
                                            <InfoOutlinedIcon
                                                fontSize="small"
                                                sx={{
                                                    color: 'text.secondary',
                                                    cursor: 'pointer'
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                }
                            />
                            )}
                            {shouldShowVisibleToParentsOption && (
                                <FormControlLabel
                                    sx={{
                                        gap: 1,
                                    }}
                                    control={
                                        <Checkbox
                                            sx={{
                                                padding: 0,
                                                marginLeft: 1
                                            }}
                                            checked={visibleToParents}
                                            onChange={async (e) =>
                                                await onVisibleToParentsChange(e.target.checked)
                                            }
                                            disabled={isFormArchived}
                                        />
                                    }
                                    label={
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}>
                                            Show this form to parents
                                            <Tooltip
                                                title="When ticked, parents can see this form in Request & Responses and on the student profile after registration. When unticked, parents can still submit via their public link but will not see it in their account."
                                                placement="right"
                                            >
                                                <InfoOutlinedIcon
                                                    fontSize="small"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            )}
                            {shouldShowShareResponseWithOtherParentsOption && (
                                <FormControlLabel
                                    sx={{
                                        gap: 1,
                                    }}
                                    control={
                                        <Checkbox
                                            sx={{
                                                padding: 0,
                                                marginLeft: 1
                                            }}
                                            checked={shareResponseWithOtherParents}
                                            onChange={async (e) =>
                                                await onShareResponseWithOtherParentsChange(e.target.checked)
                                            }
                                            disabled={isFormArchived}
                                        />
                                    }
                                    label={
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}>
                                            Share submitted response with other parents
                                            <Tooltip
                                                title="When ticked, all assigned parents can see the submitted response. When unticked, only the parent who submitted the form can see the response."
                                                placement="right"
                                            >
                                                <InfoOutlinedIcon
                                                    fontSize="small"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            )}
                            {shouldShowProfileCompletionOption && (
                                <FormControlLabel
                                    sx={{
                                        gap: 1,
                                    }}
                                    control={
                                        <Checkbox
                                            sx={{
                                                padding: 0,
                                                marginLeft: 1
                                            }}
                                            checked={profileMustBeComplete}
                                            onChange={async (e) =>
                                                await onProfileMustBeComplete(e.target.checked)
                                            }
                                            disabled={isFormArchived}
                                        />
                                    }
                                    label={
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}>
                                            Block form submission until the student&apos;s profile is complete
                                            <Tooltip
                                                title="When ticked, the form cannot be submitted by the parent until they've confirmed and completed all student profile sections."
                                                placement="right"
                                            >
                                                <InfoOutlinedIcon
                                                    fontSize="small"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            )}
                        </Stack>
                    </SettingsSectionBase>
                )}
                {shouldShowLinkedProfileVisibilityOption && (
                    <SettingsSectionBase
                        header="Linked Person"
                        headerIcon={<SupervisorAccountIcon fontSize="small"/>}
                    >
                        <Stack spacing={2}>
                            {shouldShowLinkedProfileVisibilityOption && (
                                <FormControlLabel
                                    sx={{gap: 1}}
                                    control={
                                        <Checkbox
                                            sx={{
                                                padding: 0,
                                                marginLeft: 1
                                            }}
                                            checked={!!showResponsesInLinkedProfile}
                                            onChange={(e) =>
                                                onShowResponsesInLinkedProfileChange?.(e.target.checked)
                                            }
                                            disabled={isFormArchived}
                                        />
                                    }
                                    label={
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}>
                                            Show submitted responses on the linked person&apos;s profile
                                            <Tooltip
                                                title="When ticked, the submitted responses appear on the linked student's or employee's profile, where they can be read but not added to. When unticked, they are visible only to the staff this form is assigned to."
                                                placement="right"
                                            >
                                                <InfoOutlinedIcon
                                                    sx={{
                                                        fontSize: 18,
                                                        color: 'text.secondary'
                                                    }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            )}
                            {shouldShowLinkedParentVisibilityOption && (
                                <FormControlLabel
                                    sx={{gap: 1}}
                                    control={
                                        <Checkbox
                                            sx={{
                                                padding: 0,
                                                marginLeft: 1
                                            }}
                                            checked={!!showResponsesToLinkedParents}
                                            onChange={(e) =>
                                                onShowResponsesToLinkedParentsChange?.(e.target.checked)
                                            }
                                            disabled={isFormArchived || !showResponsesInLinkedProfile}
                                        />
                                    }
                                    label={
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}>
                                            Let the child&apos;s parents see the submitted responses
                                            <Tooltip
                                                title="When ticked, parents see the submitted responses on their child's profile. When unticked, the responses stay on the profile for staff only. This needs the tick above."
                                                placement="right"
                                            >
                                                <InfoOutlinedIcon
                                                    sx={{
                                                        fontSize: 18,
                                                        color: 'text.secondary'
                                                    }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            )}
                        </Stack>
                    </SettingsSectionBase>
                )}
                <FormAccessSettings
                    embedded
                    ref={formAccessRef}
                    formId={formId}
                    formName={name}
                />
                {shouldShowApprovalSettings && (
                    <ApprovalSettings
                        embedded
                        formId={formId}
                        schema={schema}
                        isFormLive={isFormLive}
                        isFormPrivate={accessLevel === FORM_ACCESS_LEVELS.private}
                        isFormArchived={isFormArchived}
                    />
                )}
                {!isCommunicationMode && (
                    <AutoresponderSettings
                        embedded
                        formId={formId}
                        schema={schema}
                        enableAutoresponder={enableAutoresponder}
                        onAutoresponderToggle={onAutoresponderToggle}
                        accessLevel={accessLevel}
                        isFormArchived={isFormArchived}
                        audience={audience}
                        recipientType={recipientType}
                        linkedToStudents={linkToStudents}
                        linkedToEmployees={linkToEmployees}
                    />
                )}
            </Box>

            {
                audience === FORM_AUDIENCE_TYPE.none
                    ? null
                    : <Box sx={{pt: 3, width: '100%', maxWidth: '1800px', mx: 'auto'}}>
                        {
                            !enableTrigger || isCommunicationMode
                                ? <RegularFormSettings
                                    formId={formId}
                                    audience={audience}
                                    parentAudienceOption={currentParentAudienceOption}
                                    settings={settings}
                                    originalSettings={originalRecipientSettings}
                                    strictRecipientsFiltering={strictRecipientsFiltering}
                                    isFormLive={isFormLive}
                                    isFormArchived={isFormArchived}
                                    accessLevel={accessLevel}
                                    externalUsers={externalUsers}
                                    onSettingsChange={onSettingsChange}
                                    onSetStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                                    isCommunicationForm={isCommunicationForm}
                                    linkToStudents={linkToStudents}
                                    linkToEmployees={linkToEmployees}
                                    allLinkedEmployeesCanSubmit={allLinkedEmployeesCanSubmit}
                                    onAllLinkedEmployeesCanSubmitChange={onAllLinkedEmployeesCanSubmitChange}
                                    hasInitialisedRecipients={hasInitialisedRecipients}
                                    sendToPriority1ContactsOnly={sendToPriority1ContactsOnly}
                                    onRollOnly={onRollOnly}
                                />
                                : <TriggerFormSettings
                                    formId={formId}
                                    formQuestionAssigment={formQuestionAssigment}
                                    settings={settings}
                                    audience={audience}
                                    linkToStudents={linkToStudents}
                                    linkToEmployees={linkToEmployees}
                                    externalUsers={externalUsers}
                                    onSettingsChange={onSettingsChange}
                                />
                        }
                    </Box>
            }
        </Box>
    );
};

PrivateFormSettings.defaultProps = {
    isFormLive: false,
};

PrivateFormSettings.propTypes = {
    formId: PropTypes.number,
    name: PropTypes.string,
    onNameChange: PropTypes.func,
    expirationDate: PropTypes.string,
    remindInterval: PropTypes.number,
    onRemindIntervalChange: PropTypes.func,
    onExpirationDateChange: PropTypes.func,
    categories: PropTypes.array,
    onCategoriesChange: PropTypes.func,
    formCategories: PropTypes.array,
    isFormLive: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    strictRecipientsFiltering: PropTypes.bool,
    audience: PropTypes.number,
    parentAudienceType: PropTypes.number,
    audienceOptions: PropTypes.array,
    settings: PropTypes.object,
    originalRecipientSettings: PropTypes.object,
    externalUsers: PropTypes.array,
    allParentsCanSubmit: PropTypes.bool,
    sendToPriority1ContactsOnly: PropTypes.bool,
    onRollOnly: PropTypes.bool,
    visibleToParents: PropTypes.bool,
    shareResponseWithOtherParents: PropTypes.bool,
    showResponsesInLinkedProfile: PropTypes.bool,
    onShowResponsesInLinkedProfileChange: PropTypes.func,
    showResponsesToLinkedParents: PropTypes.bool,
    onShowResponsesToLinkedParentsChange: PropTypes.func,
    profileMustBeComplete: PropTypes.bool,
    onProfileMustBeComplete: PropTypes.func,
    allowMultipleSubmissions: PropTypes.bool,
    accessLevel: PropTypes.number,
    notificationEmails: PropTypes.array,
    onSettingsChange: PropTypes.func,
    onGroupFiltersChange: PropTypes.func,
    onStudentFiltersChange: PropTypes.func,
    onSetStrictRecipientsFiltering: PropTypes.func,
    onAudienceChange: PropTypes.func,
    onParentAudienceTypeChange: PropTypes.func,
    onAllParentsCanSubmitChange: PropTypes.func,
    onSendToPriority1ContactsOnlyChange: PropTypes.func,
    onVisibleToParentsChange: PropTypes.func,
    onShareResponseWithOtherParentsChange: PropTypes.func,
    onAllowMultipleSubmissionsChange: PropTypes.func,
    onAccessLevelChange: PropTypes.func,
    onNotificationEmailsChange: PropTypes.func,
    enableRecipientNotifications: PropTypes.bool,
    onEnableRecipientNotificationsChange: PropTypes.func,
    isCommunicationForm: PropTypes.bool,
    onCommunicationTypeChange: PropTypes.func,
    canSwitchToEmail: PropTypes.bool,
    enableTrigger: PropTypes.bool,
    setEnableTrigger: PropTypes.func,
    formQuestionAssigment: PropTypes.object,
    linkToStudents: PropTypes.bool,
    onLinkToStudentsChange: PropTypes.func,
    linkToEmployees: PropTypes.bool,
    onLinkToEmployeesChange: PropTypes.func,
    hasInitialisedRecipients: PropTypes.bool,
    allLinkedEmployeesCanSubmit: PropTypes.bool,
    onAllLinkedEmployeesCanSubmitChange: PropTypes.func,
    formAccessRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.any})
    ]),
    schema: PropTypes.array,
    enableAutoresponder: PropTypes.bool,
    onAutoresponderToggle: PropTypes.func,
    recipientType: PropTypes.string,
};

export default PrivateFormSettings;
